import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges
} from '@angular/core';
import { Subscription } from 'rxjs';
import * as moment from 'moment';
import { Moment } from 'moment';
import { ProcurementAnalyticsService } from '../../procurement/analytics/procurement-analytics.service';
import {
  AnalyticsPeriod,
  AnalyticsReport,
  AnalyticsSeries,
  AnalyticsAnalogues,
  AnalogueSource,
  AnalyticsMetrics,
  AnalyticsRow,
  AnalyticsWarehouseSales,
  AnalyticsWarehouseMinimumStock
} from '../../procurement/analytics/procurement-analytics.types';
import {
  analyticsDefaults,
  analyticsNumber,
  analyticsQuantityValue,
  kyivDate,
  shiftDate,
  validAnalyticsDate
} from '../../procurement/analytics/procurement-analytics.util';
import { ProductMinimumStockChange } from '../../procurement/analytics/procurement-minimum-stock.component';

@Component({
  selector: 'app-product-sales',
  templateUrl: './product-sales.component.html',
  styleUrls: ['./product-sales.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductSalesComponent implements OnChanges, OnDestroy {
  @Input() productId = 0;
  @Input() productUnit = 'шт.';
  @Input() period: AnalyticsPeriod | null = null;
  @Input() minimumStockChanges: Record<number, AnalyticsWarehouseMinimumStock> = {};
  @Output() minimumStockSaved = new EventEmitter<ProductMinimumStockChange>();
  filters = analyticsDefaults();
  applied = analyticsDefaults();
  readonly maxDate = shiftDate(kyivDate(), -1);
  readonly calendarMaxDate = moment(this.maxDate, 'YYYY-MM-DD', true);
  readonly number = analyticsNumber;
  readonly unit = 'шт.';
  loading = false;
  seriesLoading = false;
  resolving = false;
  includeAnalogues = true;
  appliedAnalogues = true;
  readonly analogueSources: AnalogueSource[] = ['MANUAL', 'TECDOC'];
  groupSources: AnalogueSource[] = [];
  resolution: AnalyticsAnalogues | null = null;
  suggestionsReady = false;
  analoguesError = '';
  error = '';
  seriesError = '';
  validationError = '';
  report: AnalyticsReport | null = null;
  series: AnalyticsSeries | null = null;
  chart: Array<{ date: string; soldHeight: number; returnedHeight: number; title: string }> = [];
  private requests = new Subscription();
  private warehouseMetrics = new Map<string, AnalyticsMetrics>();
  private warehouseLeaders = new Map<number | null, Set<number>>();
  private salesLeaderNet: number | null = null;
  private reportPage = 1;
  private readonly emptyMetrics: AnalyticsMetrics = {
    soldQty: 0, returnedQty: 0, netQty: 0, shipmentCount: 0, revenue: 0,
    cogs: 0, profit: 0, marginPercent: null,
    costKnownRows: 0, costEstimatedRows: 0, costUnknownRows: 0, quantities: []
  };

  constructor(
    private readonly api: ProcurementAnalyticsService,
    private readonly cd: ChangeDetectorRef
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.productId || changes.period) {
      if (this.period) {
        this.filters = { ...this.filters, ...this.period };
      }
      this.requests.unsubscribe();
      this.requests = new Subscription();
      this.report = null;
      this.resetWarehouseSales();
      this.series = null;
      this.chart = [];
      this.loading = false;
      this.seriesLoading = false;
      this.resolving = false;
      this.resolution = null;
      this.suggestionsReady = false;
      this.analoguesError = '';
      this.applied = { ...this.applied, productIds: [this.productId] };
      this.load();
    }
  }

  ngOnDestroy(): void {
    this.requests.unsubscribe();
  }

  setCalendarDate(field: 'dateFrom' | 'dateTo', value: Moment | null): void {
    this.filters[field] = value?.isValid() ? value.format('YYYY-MM-DD') : '';
  }

  preset(days: number): void {
    this.filters.dateFrom = shiftDate(kyivDate(), -days);
    this.filters.dateTo = shiftDate(kyivDate(), -1);
    this.load();
  }

  resetFilters(): void {
    if (this.loading || this.seriesLoading) return;
    this.filters = analyticsDefaults();
    this.includeAnalogues = true;
    this.load();
  }

  load(): void {
    this.validationError = '';
    if (
      !validAnalyticsDate(this.filters.dateFrom) ||
      !validAnalyticsDate(this.filters.dateTo) ||
      this.filters.dateFrom > this.filters.dateTo ||
      this.filters.dateTo > this.maxDate
    ) {
      this.validationError = 'Выберите корректный период из завершённых дней.';
      return;
    }
    if (!Number.isSafeInteger(this.productId) || this.productId <= 0) return;
    this.requests.unsubscribe();
    this.requests = new Subscription();
    this.applied = {
      ...this.filters,
      productIds: [this.productId],
      warehouseIds: [],
      brandIds: [],
      noSales: false,
      page: 1,
      pageSize: 200,
      sortBy: 'netQty',
      sortDirection: 'desc'
    };
    this.report = null;
    this.resetWarehouseSales();
    this.series = null;
    this.chart = [];
    this.error = '';
    this.seriesError = '';
    this.analoguesError = '';
    this.resolution = null;
    this.suggestionsReady = false;
    this.appliedAnalogues = this.includeAnalogues;
    this.groupSources = this.appliedAnalogues ? this.analogueSources : [];
    this.loading = true;
    this.seriesLoading = true;
    this.resolving = this.appliedAnalogues;
    if (this.appliedAnalogues) {
      this.requests.add(
        this.api.analogues(this.productId, this.analogueSources, true).subscribe({
          next: (result) => {
            this.resolution = result;
            this.applied = {
              ...this.applied,
              productIds: [...new Set([this.productId, ...result.productIds])]
            };
            this.resolving = false;
            this.loadResolved();
            this.cd.markForCheck();
          },
          error: () => {
            this.resolving = false;
            this.loading = false;
            this.seriesLoading = false;
            this.analoguesError =
              'Не удалось загрузить аналоги. Повторите запрос или отключите их учёт.';
            this.cd.markForCheck();
          }
        })
      );
      return;
    }
    this.loadResolved();
  }

  private loadResolved(): void {
    this.suggestionsReady = true;
    this.loadReport();
    this.requests.add(
      this.api.series(this.applied).subscribe({
        next: (result) => {
          this.series = result;
          const maximum = Math.max(
            1,
            ...result.points.map((point) =>
              Math.max(
                this.quantityValue(point.metrics, 'soldQty') || 0,
                this.quantityValue(point.metrics, 'returnedQty') || 0
              )
            )
          );
          this.chart = result.points.map((point) => ({
            date: point.date,
            soldHeight:
              (Math.max(0, this.quantityValue(point.metrics, 'soldQty') || 0) / maximum) * 100,
            returnedHeight:
              (Math.max(0, this.quantityValue(point.metrics, 'returnedQty') || 0) / maximum) * 100,
            title: `${point.date}: отгружено ${this.number(
              this.quantityValue(point.metrics, 'soldQty')
            )} шт., возвращено ${this.number(this.quantityValue(point.metrics, 'returnedQty'))} шт.`
          }));
          this.seriesLoading = false;
          this.cd.markForCheck();
        },
        error: () => {
          this.seriesLoading = false;
          this.seriesError = 'Динамика продаж недоступна. Обновите отчёт.';
          this.cd.markForCheck();
        }
      })
    );
  }

  private loadReport(): void {
    this.requests.add(
      this.api.report(this.applied, true, this.appliedAnalogues).subscribe({
        next: (result) => {
          this.report = result;
          this.reportPage = this.applied.page;
          this.warehouseMetrics.clear();
          this.warehouseLeaders.clear();
          result.rows.forEach((row) => row.warehouseSales?.forEach((sale) => {
            this.warehouseMetrics.set(this.warehouseKey(row, sale.warehouseId), sale.metrics);
          }));
          result.warehouses?.forEach((warehouse) => {
            this.warehouseLeaders.set(warehouse.warehouseId, new Set(warehouse.leaders.map((leader) => leader.productId)));
          });
          if (this.applied.page === 1) {
            this.salesLeaderNet = Math.max(0, ...result.rows.map((row) => this.quantityValue(row.metrics, 'netQty'))) || null;
          }
          this.loading = false;
          this.cd.markForCheck();
        },
        error: () => {
          this.loading = false;
          if (this.report) this.applied = { ...this.applied, page: this.reportPage };
          this.error = 'Не удалось загрузить продажи. Повторите запрос.';
          this.cd.markForCheck();
        }
      })
    );
  }

  get warehouses(): AnalyticsWarehouseSales[] {
    return this.report?.warehouses || [];
  }

  get hasUnassignedWarehouse(): boolean {
    return this.warehouses.some((warehouse) => warehouse.warehouseId === null);
  }

  private resetWarehouseSales(): void {
    this.warehouseMetrics.clear();
    this.warehouseLeaders.clear();
    this.salesLeaderNet = null;
    this.reportPage = 1;
  }

  private warehouseKey(row: AnalyticsRow, warehouseId: number | null): string {
    return `${row.productId || row.id}:${warehouseId ?? 'unknown'}`;
  }

  warehouseSale(row: AnalyticsRow, warehouseId: number | null): AnalyticsMetrics {
    return this.warehouseMetrics.get(this.warehouseKey(row, warehouseId)) || this.emptyMetrics;
  }

  isWarehouseLeader(row: AnalyticsRow, warehouseId: number | null): boolean {
    return this.warehouseLeaders.get(warehouseId)?.has(row.productId || row.id) || false;
  }

  isSalesLeader(row: AnalyticsRow): boolean {
    return this.salesLeaderNet !== null && this.quantityValue(row.metrics, 'netQty') === this.salesLeaderNet;
  }

  memberPage(page: number): void {
    if (this.loading || this.seriesLoading || !this.report || !Number.isInteger(page)
      || page < 1 || page > Math.ceil(this.report.totalRows / this.applied.pageSize)
      || page === this.applied.page) return;
    this.applied = { ...this.applied, page };
    this.loading = true;
    this.error = '';
    this.loadReport();
  }

  private normalizeUnit(unit: string): string {
    const value = (unit || '').trim() || 'шт.';
    return /^шт\.?$/i.test(value) ? 'шт.' : value;
  }

  get stockUnit(): string {
    return this.normalizeUnit(this.productUnit);
  }

  quantityValue(
    metrics: AnalyticsMetrics,
    field: 'soldQty' | 'returnedQty' | 'netQty'
  ): number {
    return analyticsQuantityValue(metrics, field);
  }

  profitCostNote(metrics: AnalyticsMetrics): string {
    if (metrics.profit === null) return 'нет себестоимости';
    if (metrics.costUnknownRows > 0) return 'частично';
    if (metrics.costEstimatedRows > 0) return 'оценка';
    return '';
  }

  profitTooltip(metrics: AnalyticsMetrics): string {
    if (metrics.profit === null) {
      return 'Валовая прибыль не определена: нет себестоимости проданных товаров.';
    }
    let text = 'Выручка − себестоимость проданных товаров, с учётом возвратов.';
    if (metrics.costUnknownRows > 0) {
      text += ` Частичный расчёт. Строк без себестоимости: ${metrics.costUnknownRows}.`;
    }
    if (metrics.costEstimatedRows > 0) {
      text += ` Строк с оценочной себестоимостью: ${metrics.costEstimatedRows}.`;
    }
    return text;
  }

  quantityLabel(metrics: AnalyticsMetrics, field: 'soldQty' | 'returnedQty' | 'netQty'): string {
    return this.number(this.quantityValue(metrics, field));
  }

  sourceLabel(productId: number): string {
    if (productId === this.productId) return 'Этот товар';
    const sources = new Set<AnalogueSource>();
    for (const candidate of this.resolution?.candidates || []) {
      if (candidate.matchStatus === 'matched' && candidate.productIds.includes(productId)) {
        candidate.evidence.forEach((evidence) => sources.add(evidence.source));
      }
    }
    return [sources.has('TECDOC') ? 'TecDoc' : '', sources.has('MANUAL') ? 'Ручной' : '']
      .filter(Boolean)
      .join(' + ');
  }

  get unmatchedCount(): number {
    return (
      this.resolution?.candidates.filter((candidate) => candidate.matchStatus === 'unmatched')
        .length || 0
    );
  }

  get ambiguousCount(): number {
    return this.ambiguousCandidates.length;
  }

  get ambiguousCandidates() {
    return (
      this.resolution?.candidates.filter((candidate) => candidate.matchStatus === 'ambiguous') || []
    );
  }

  get periodDays(): number {
    return (Date.parse(this.applied.dateTo) - Date.parse(this.applied.dateFrom)) / 86400000 + 1;
  }

  get averageDailyQty(): number | null {
    const netQty = this.report ? this.quantityValue(this.report.totals, 'netQty') : null;
    return typeof netQty === 'number' ? netQty / this.periodDays : null;
  }
}
